/**
 * appCardUtils — pure helpers for rendering an application card.
 *
 * These functions are extracted from the inline Cloudscape `Cards`
 * `cardDefinition` closures/locals in `Home/Apps.jsx` so that both the
 * Application Marketplace and Quick Search can share identical card logic,
 * and so the correctness properties (Properties 2, 3, 5) can target a pure
 * surface directly.
 *
 * IMPORTANT: This module has NO React/DOM dependency. Every export is a pure
 * function (or a plain constant) and is importable in a plain Node/Jest
 * environment without rendering anything.
 *
 * Normalizations applied relative to the legacy inline card:
 *  - DD-1: truncation boundary is `> 200` (legacy used `< 200`) and uses a
 *          single ellipsis character `…` (legacy used three dots `...`).
 *  - DD-2: any status other than `Active`/`WIP`/`Deprecated` (absent, empty,
 *          whitespace, unrecognized) maps to the single `status-warning`
 *          "No status" indicator (legacy rendered nothing for unknown
 *          truthy values).
 */

/**
 * Cycle of CSS classes applied to `use_case` label chips, indexed by
 * `index % LABEL_COLOR_CLASSES.length`. Mirrors the local in `Apps.jsx`.
 * @type {string[]}
 */
export const LABEL_COLOR_CLASSES = ['card-label-blue', 'card-label-grey', 'card-label-red'];

/**
 * Maximum number of characters shown before a description is truncated (DD-1).
 * @type {number}
 */
export const DESCRIPTION_MAX_LENGTH = 200;

/** Single ellipsis character appended to truncated descriptions (DD-1). */
export const ELLIPSIS = '…';

/**
 * Truncate an application description per DD-1.
 *
 * Returns the full string when its length is <= 200, otherwise the first 200
 * characters followed by a SINGLE ellipsis character `…`. Null/undefined/empty
 * and non-string inputs are handled gracefully without throwing.
 *
 * @param {string} desc - The raw description.
 * @returns {string} The (possibly truncated) description; `""` for nullish input.
 */
export const truncateDescription = (desc) => {
  if (desc == null) {
    return '';
  }
  const str = typeof desc === 'string' ? desc : String(desc);
  return str.length > DESCRIPTION_MAX_LENGTH
    ? `${str.slice(0, DESCRIPTION_MAX_LENGTH)}${ELLIPSIS}`
    : str;
};

/**
 * @typedef {Object} StatusIndicator
 * @property {string} type    Cloudscape `Icon` name (e.g. `status-positive`).
 * @property {string} variant Cloudscape `Icon` variant (e.g. `success`).
 * @property {string} color   Inline text color used by the card.
 * @property {string} label   Visible label text.
 */

/**
 * Map an App_Record `status` to its Cloudscape indicator per DD-2.
 *
 * ONLY `Active`/`WIP`/`Deprecated` map to their specific indicator + color
 * (matching the legacy inline card). Everything else — absent, empty,
 * whitespace, non-string, or any unrecognized string — maps to the single
 * `status-warning` "No status" indicator.
 *
 * @param {string} status - The App_Record status value.
 * @returns {StatusIndicator} The resolved indicator descriptor.
 */
export const statusToIndicator = (status) => {
  switch (status) {
    case 'Deprecated':
      return { type: 'status-negative', variant: 'error', color: '#db0000', label: 'Deprecated' };
    case 'WIP':
      return { type: 'status-pending', variant: 'normal', color: '#0f141a', label: 'WIP' };
    case 'Active':
      return { type: 'status-positive', variant: 'success', color: '#00802f', label: 'Active' };
    default:
      return { type: 'status-warning', variant: 'warning', color: '#855900', label: 'No status' };
  }
};

/**
 * Format a view count for display, mirroring the legacy `formatViewCount`
 * closure plus the inline `view_count != null ? ... : "0"` guard.
 *
 * Returns `"0"` when the count is null/undefined. Otherwise formats with `M`
 * (>= 1,000,000) or `k` (>= 1,000) suffixes, falling back to the plain number
 * as a string.
 *
 * @param {number|null|undefined} view_count - The raw view count.
 * @returns {string} The formatted count.
 */
export const formatViewCount = (view_count) => {
  if (view_count == null) {
    return '0';
  }
  if (view_count >= 1000000) {
    return `${(view_count / 1000000).toFixed(1)}M`;
  }
  if (view_count >= 1000) {
    return `${(view_count / 1000).toFixed(1)}k`;
  }
  return `${view_count}`;
};

/**
 * Format a run count for display, using the same suffix logic as
 * `formatViewCount` (`k` >= 1,000; `M` >= 1,000,000).
 *
 * Returns `null` when the count is null, undefined, 0, or empty string — the
 * caller uses `null` to decide whether to render the run-count section at all.
 *
 * @param {number|string|null|undefined} run_count - The raw run count.
 * @returns {string|null} The formatted count, or `null` when absent/zero.
 */
export const formatRunCount = (run_count) => {
  if (run_count == null || run_count === '' || run_count === 0 || run_count === '0') {
    return null;
  }
  const num = typeof run_count === 'number' ? run_count : Number(run_count);
  if (Number.isNaN(num) || num <= 0) {
    return null;
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return `${num}`;
};

/**
 * Whether an App_Record `url` is usable as a destination (DD-1).
 *
 * Returns `true` only when `url` is a string containing at least one
 * non-whitespace character. An absent, empty, whitespace-only, or non-string
 * `url` is treated as absent so resolution falls through to the
 * `App/{app_id}` route. This tightens the legacy truthiness check to satisfy
 * Requirement 3.1/3.2.
 *
 * @param {string} url - The raw App_Record `url`.
 * @returns {boolean} `true` when `url` has at least one non-whitespace char.
 */
export const hasUsableUrl = (url) => typeof url === 'string' && url.trim().length > 0;

/**
 * Normalize a resolved destination so an in-app route is always rooted at the
 * site origin rather than the current page.
 *
 * A stored `url` like `App/1679` (no leading slash) is a RELATIVE reference: a
 * native `<a href>` or router `<Link>` resolves it against the current page's
 * directory, so from a nested page such as `/ClosePortal/HOME/Page/...` it
 * becomes `/ClosePortal/HOME/Page/App/1679` — a route that matches nothing and
 * renders a blank page. From the top-level `/` it happens to resolve correctly
 * to `/App/1679`, which is why the breakage is path-dependent and easy to miss.
 *
 * This helper leaves untouched anything that is already an absolute target:
 *  - external/absolute URLs (`http(s)://…`)
 *  - protocol-relative URLs (`//host/…`)
 *  - other explicit schemes (`mailto:`, `tel:`, …)
 *  - paths already rooted at the origin (leading `/`)
 *
 * Everything else is treated as an in-app relative path and rooted with a
 * single leading `/` so it resolves the same way from every page.
 *
 * @param {string} url - The resolved destination.
 * @returns {string} The destination rooted at the origin when it was a
 *   relative in-app path; otherwise the input unchanged.
 */
export const normalizeInAppUrl = (url) => {
  if (typeof url !== 'string') {
    return url;
  }
  const trimmed = url.trim();
  if (trimmed.length === 0) {
    return url;
  }
  // Already an absolute target: external URL, protocol-relative URL, an
  // explicit scheme (mailto:/tel:/…), or an origin-rooted path.
  if (
    isExternalUrl(trimmed) ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('/') ||
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)
  ) {
    return trimmed;
  }
  // In-app relative path (e.g. `App/1679`): root it at the origin so it
  // resolves identically from any page.
  return `/${trimmed}`;
};

/**
 * Resolve the click-through destination for an App_Record, mirroring the
 * legacy `getUrl` closure: the record's `url` when present and non-empty,
 * otherwise the `App/{app_id}` route. Null/undefined `item` is handled
 * gracefully without throwing.
 *
 * Delegates to `hasUsableUrl` so a whitespace-only `url` falls through to the
 * `App/{app_id}` route, keeping card resolution identical to
 * `resolveAppDestination` (DD-1). A relative in-app `url` is normalized to an
 * origin-rooted path via `normalizeInAppUrl` so it doesn't resolve against the
 * current (possibly nested) page.
 *
 * @param {{ url?: string, app_id?: (string|number) }} item - The App_Record.
 * @returns {string} The resolved destination path.
 */
export const resolveUrl = (item) => {
  if (item && hasUsableUrl(item.url)) {
    return normalizeInAppUrl(item.url);
  }
  return `/App/${item ? item.app_id : undefined}`;
};

/**
 * Resolve an application record to its App_Destination for hyperlinking,
 * mirroring the Application Marketplace rule (Req 3.3):
 *  - `app.url` when present and non-whitespace  (Req 3.1, 3.4)
 *  - otherwise the `App/{app_id}` route         (Req 3.2, 3.5)
 *
 * Uses `hasUsableUrl` so a whitespace-only `url` falls through to the route,
 * keeping link resolution identical to the card path (`resolveUrl`). A
 * relative in-app `url` is normalized to an origin-rooted path via
 * `normalizeInAppUrl` so it doesn't resolve against the current (possibly
 * nested) page. Null/undefined `app` is handled gracefully without throwing.
 *
 * @param {{ url?: string, app_id?: (string|number) }} app - The App_Record.
 * @returns {string} The resolved App_Destination.
 */
export const resolveAppDestination = (app) =>
  hasUsableUrl(app?.url) ? normalizeInAppUrl(app.url) : `/App/${app?.app_id}`;

/**
 * Whether a resolved destination is an absolute (external) URL.
 *
 * React Router's `<Link to={...}>` treats its target as a relative path
 * segment, so an absolute URL like `https://example.com/apps/interco` would be
 * mangled into `/https://example.com/apps/interco`. Callers use this to decide
 * between a plain `<a href>` (absolute URLs) and a router `<Link>` (in-app
 * routes).
 *
 * @param {string} url - The resolved destination.
 * @returns {boolean} `true` when `url` starts with `http://` or `https://`.
 */
export const isExternalUrl = (url) => typeof url === 'string' && /^https?:\/\//.test(url);
